import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Mail, ChevronRight } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ugatepass-landing/artifacts/vg9ku1n9_Logo%20for%20Web.png";

const SUPPORT_EMAIL = "unigateappinfo@gmail.com";

const Section = ({ id, title, children }) => (
  <section id={id} data-testid={`policy-section-${id}`} className="scroll-mt-24">
    <h2 className="text-2xl sm:text-3xl font-extrabold text-[#23395D] tracking-tight">
      {title}
    </h2>
    <div className="mt-4 text-slate-700 leading-relaxed text-[16px] space-y-3">
      {children}
    </div>
  </section>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2.5">
    <ChevronRight className="w-4 h-4 text-[#4CAF50] mt-1 flex-shrink-0" />
    <span>{children}</span>
  </li>
);

const PrivacyPolicy = () => {
  const toc = [
    { id: "introduction", label: "Introduction" },
    { id: "information-we-collect", label: "Information We Collect" },
    { id: "how-we-use-information", label: "How We Use Information" },
    { id: "data-storage", label: "Data Storage" },
    { id: "data-sharing", label: "Data Sharing" },
    { id: "data-security", label: "Data Security" },
    { id: "user-responsibilities", label: "User Responsibilities" },
    { id: "data-retention", label: "Data Retention" },
    { id: "changes-to-policy", label: "Changes to this Policy" },
    { id: "contact-us", label: "Contact Us" },
  ];

  return (
    <div data-testid="privacy-policy-page" className="bg-white text-[#23395D]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="privacy-nav-logo"
            className="flex items-center gap-2"
          >
            <img src={LOGO_URL} alt="UGatePass" className="h-10 w-10 object-contain" />
            <span className="font-extrabold text-[#23395D] text-lg tracking-tight">
              UGate<span className="text-[#4CAF50]">Pass</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-[#1F5FBF] hover:bg-[#F5F8FC] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </header>

      {/* Hero band */}
      <section
        data-testid="privacy-hero"
        className="relative overflow-hidden bg-gradient-to-br from-[#1F5FBF] via-[#3A7BD5] to-[#4CAF50] text-white"
      >
        <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_20%_20%,white_1px,transparent_1px)] [background-size:24px_24px]" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/25 text-sm font-medium">
            <Shield className="w-4 h-4" /> Privacy &amp; Trust
          </span>
          <h1
            data-testid="privacy-heading"
            className="mt-6 text-4xl sm:text-5xl font-extrabold leading-[1.1] tracking-tight"
          >
            Privacy Policy
          </h1>
          <p className="mt-4 text-white/90 text-lg max-w-2xl leading-relaxed">
            How UGatePass collects, uses, stores and protects your information.
          </p>
          <div
            data-testid="privacy-last-updated"
            className="mt-5 inline-block text-sm font-medium px-3 py-1.5 rounded-full bg-white/15 border border-white/25"
          >
            Last Updated: January 2026
          </div>
        </div>
      </section>

      {/* Body grid */}
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-[260px_1fr] gap-10 lg:gap-14">
        {/* TOC */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div
            data-testid="privacy-toc"
            className="p-5 rounded-2xl bg-[#F5F8FC] border border-slate-200"
          >
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#1F5FBF] mb-3">
              On this page
            </div>
            <nav className="flex flex-col gap-1.5">
              {toc.map((t) => (
                <a
                  key={t.id}
                  href={`#${t.id}`}
                  data-testid={`privacy-toc-${t.id}`}
                  className="text-sm text-slate-700 hover:text-[#1F5FBF] hover:bg-white rounded-lg px-3 py-1.5 transition-colors"
                >
                  {t.label}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <article className="space-y-10 max-w-3xl">
          <Section id="introduction" title="Introduction">
            <p>
              UGatePass is a digital Entry and Exit Management application
              designed to manage visitors, members, vehicles, and gate access
              for facilities such as apartments, communities, schools, offices,
              hospitals, hostels, factories, and events.
            </p>
            <p>
              By using UGatePass, you agree to the collection and use of
              information as described in this Privacy Policy.
            </p>
          </Section>

          <Section id="information-we-collect" title="Information We Collect">
            <p>UGatePass may collect:</p>
            <ul className="space-y-2 mt-2">
              <Bullet>Name</Bullet>
              <Bullet>Mobile number</Bullet>
              <Bullet>Email address</Bullet>
              <Bullet>User role and facility information</Bullet>
              <Bullet>Visitor details</Bullet>
              <Bullet>Vehicle details</Bullet>
              <Bullet>Entry and exit records</Bullet>
              <Bullet>Photographs and images captured through the application (if enabled)</Bullet>
              <Bullet>QR code related information</Bullet>
            </ul>
          </Section>

          <Section id="how-we-use-information" title="How We Use Information">
            <p>The collected information is used to:</p>
            <ul className="space-y-2 mt-2">
              <Bullet>Manage facility access</Bullet>
              <Bullet>Generate and validate UGatePasses</Bullet>
              <Bullet>Maintain entry and exit records</Bullet>
              <Bullet>Improve security and visitor management</Bullet>
              <Bullet>Provide support and system administration</Bullet>
            </ul>
          </Section>

          <Section id="data-storage" title="Data Storage">
            <p>
              Information may be stored securely on cloud servers and databases
              used by UGatePass.
            </p>
          </Section>

          <Section id="data-sharing" title="Data Sharing">
            <p>UGatePass does not sell personal information.</p>
            <p>Information may be shared only:</p>
            <ul className="space-y-2 mt-2">
              <Bullet>With authorized facility administrators</Bullet>
              <Bullet>With authorized gate staff</Bullet>
              <Bullet>When required by law</Bullet>
            </ul>
          </Section>

          <Section id="data-security" title="Data Security">
            <p>
              We take reasonable measures to protect user information from
              unauthorized access, alteration, or disclosure.
            </p>
          </Section>

          <Section id="user-responsibilities" title="User Responsibilities">
            <p>Users are responsible for:</p>
            <ul className="space-y-2 mt-2">
              <Bullet>Maintaining the confidentiality of login credentials</Bullet>
              <Bullet>Providing accurate information</Bullet>
              <Bullet>Reporting unauthorized account usage</Bullet>
            </ul>
          </Section>

          <Section id="data-retention" title="Data Retention">
            <p>
              Entry, exit, visitor, and vehicle records may be retained as
              required by the facility administration and applicable
              regulations.
            </p>
          </Section>

          <Section id="changes-to-policy" title="Changes to this Policy">
            <p>
              This Privacy Policy may be updated from time to time. Updates
              will be published within the application.
            </p>
          </Section>

          <Section id="contact-us" title="Contact Us">
            <p>
              For privacy-related questions or concerns, please contact:
            </p>
            <div
              data-testid="privacy-contact-card"
              className="mt-3 p-5 rounded-2xl bg-[#F5F8FC] border border-slate-200 flex items-center gap-4"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#1F5FBF] to-[#4CAF50] flex items-center justify-center text-white">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-[#23395D]">UGatePass Support</div>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-[#1F5FBF] font-medium hover:underline"
                  data-testid="privacy-support-email"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
            <p className="mt-4 text-slate-600 text-[15px]">
              Users may contact UGatePass Support to request information
              regarding their data, subject to facility policies and applicable
              laws.
            </p>
          </Section>

          <div className="pt-6 border-t border-slate-200">
            <Link
              to="/"
              data-testid="privacy-bottom-back"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-[#1F5FBF] to-[#4CAF50] text-white font-semibold shadow-sm hover:opacity-90 transition-all"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>
          </div>
        </article>
      </div>

      {/* Footer */}
      <footer className="bg-[#23395D] text-slate-300">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
          <div>UGate Pass © 2026. All Rights Reserved.</div>
          <Link
            to="/"
            data-testid="privacy-footer-home"
            className="hover:text-white"
          >
            Back to Home
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
